import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tradeShowService from '../../services/tradeShowService';

const STATUS_BADGE = { upcoming: 'secondary', active: 'success', completed: 'dark' };

function TradeShowFormModal({ show, onClose, onSaved }) {
  const blank = { name: '', location: '', boothNumber: '', startDate: '', endDate: '', notes: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (show) { setForm(blank); setError(''); } }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      setError('Name, start date and end date are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const saved = await tradeShowService.createTradeShow({
        name: form.name.trim(),
        location: form.location.trim() || null,
        boothNumber: form.boothNumber.trim() || null,
        startDate: form.startDate,
        endDate: form.endDate,
        notes: form.notes.trim() || null,
      });
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save trade show.');
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New Trade Show</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Show Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Revo Soccer Expo" autoFocus />
              </div>
              <div className="row g-2 mb-3">
                <div className="col-8">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>Location</label>
                  <input className="form-control form-control-sm" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Miami, FL" />
                </div>
                <div className="col-4">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>Booth #</label>
                  <input className="form-control form-control-sm" value={form.boothNumber} onChange={e => set('boothNumber', e.target.value)} />
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Start Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                </div>
                <div className="col">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>End Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>Notes</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Create Trade Show'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function TradeShowsPage() {
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    tradeShowService.listTradeShows().then(setShows).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSaved = (saved) => {
    setShows(prev => [{ ...saved, pnl: null }, ...prev]);
    setShowModal(false);
    navigate(`/trade-shows/${saved.id}`);
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 960 }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Trade Shows</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/trade-shows/analytics')}>
            📊 Year-End Analytics
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ New Trade Show</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : shows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <p className="mb-2">No trade shows yet.</p>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowModal(true)}>Log your first trade show</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Show</th>
                <th>Dates</th>
                <th className="text-end">Cost</th>
                <th className="text-end">Leads</th>
                <th className="text-end">Revenue</th>
                <th className="text-end">ROI</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shows.map(show => (
                <tr key={show.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/trade-shows/${show.id}`)}>
                  <td>
                    <div className="fw-semibold">{show.name}</div>
                    <div className="text-muted small">
                      {show.location}
                      {show.boothNumber && <> · Booth {show.boothNumber}</>}
                    </div>
                  </td>
                  <td className="small text-muted">{show.startDate} → {show.endDate}</td>
                  <td className="text-end">{money(show.pnl?.totalCost)}</td>
                  <td className="text-end">{show.pnl?.leadsCaptured ?? 0}</td>
                  <td className="text-end">{money(show.pnl?.revenueGenerated)}</td>
                  <td className="text-end">
                    {show.pnl?.roiPct == null ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span className={`badge ${show.pnl.roiPct >= 0 ? 'bg-success' : 'bg-danger'}`}>
                        {show.pnl.roiPct >= 0 ? '+' : ''}{show.pnl.roiPct.toFixed(0)}%
                      </span>
                    )}
                  </td>
                  <td><span className={`badge bg-${STATUS_BADGE[show.status] || 'secondary'} text-capitalize`}>{show.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TradeShowFormModal show={showModal} onClose={() => setShowModal(false)} onSaved={handleSaved} />
    </div>
  );
}
